import { OutpassStatus } from "./Enums";
import { UserModel } from "./UserModel";

/**
 * Outpass list paging envelope: a client-side class with a computed TotalPages,
 * distinct from CommonModel's PaginatedResult<T>, whose paging fields all arrive
 * from the server. Both shapes are in use; the name says which one this is.
 */
export class OutpassPaginatedResult<T> {
  Items: T[] = [];
  TotalCount: number = 0;
  PageNumber: number = 1;
  PageSize: number = 20;

  get TotalPages(): number {
    return Math.ceil(this.TotalCount / this.PageSize);
  }
}

/** How an out pass leaves the premises: its own vehicle, a courier, or carried by hand. 'ByHand' is shown as "By Hand". */
export type OutpassSentByMode = 'Vehicle' | 'Courier' | 'ByHand';

/** The shown label of each Sent by mode. */
export const OUTPASS_SENT_BY_LABELS: Readonly<Record<OutpassSentByMode, string>> = {
  Vehicle: 'Vehicle',
  Courier: 'Courier',
  ByHand: 'By Hand',
};

/** Who an out pass goes to: a customer, a supplier, or an "other party" typed by the user. */
export type OutpassRecipientType = 'Customer' | 'Supplier' | 'Other';

/** One row of the recipient search (outpass/searchoutpassrecipients), and the value of the recipient picker. Id is null for a new other party. */
export interface OutpassRecipientModel {
  Type: OutpassRecipientType;
  Id: number | null;
  Name: string;
}

/** Body of outpass/updatesentby: the Courier or By Hand details of an existing out pass. EditReason is required (10+ characters) once approved. */
export class OutpassSentByUpdateModel {
  OutpassId: number = 0;
  SentByMode: 'Courier' | 'ByHand' = null;
  CourierName: string = '';
  DocketNumber: string = '';
  CarrierName: string = '';
  CarrierContact: string = '';
  EditReason: string = '';
}

export class OutpassFilterVm {
  outpassToCustomerId?: number = 0;
  outpassToType?: OutpassRecipientType | '' = '';
  outpassToSupplierId?: number = 0;
  outpassToPartyId?: number = 0;
  outpassTo?: string = '';
  outpassNumber?: string = '';
  outpassType?: string = '';
  purposeId?: number = 0;
  fromDate?: Date = null;
  toDate?: Date = null;
  isOutpassIn?: boolean = null;
  outpassProductName?: string = '';
  productId?: number = 0; // Added ProductId filter
  storeId?: number = 0; // Added StoreId filter for store filtering
  statuses?: string[] = []; // Added for multi-select status filtering
  isPastDue?: boolean = null; // Dashboard drill-down filter (nullable — no effect when not provided)
  pageNumber: number = 1;
  pageSize: number = 20;
  includeItems: boolean = true;
  exportAll: boolean = false; // Added for export functionality
}

export class OutPassModel {
  SerialNo: number = 0;
  OutpassTo: string = '';
  OutpassToCustomerId: number = 0;
  OutpassNumber: string = '';
  OutpassDate: any = '';
  ExpectedReturnDate!: string;
  OutpassType: string = '';
  Purpose: string = '';
  PurposeId: number = 0;
  Remark: string = '';
  OutpassId: number = 0;
  IsOutpassIn?: boolean;
  AddedBy: string = '';
  AddedDate?: string = '';
  Status: string = '';
  TransportId: number = 0;
  VehicleId: number = 0;
  TransportName: string = '';
  VehicleNumber: string = '';
  /** How it leaves: null when not chosen yet. Vehicle uses TransportId/VehicleId; Courier uses CourierName/DocketNumber; ByHand uses CarrierName/CarrierContact. */
  SentByMode?: OutpassSentByMode | null = null;
  CourierName?: string = '';
  DocketNumber?: string = '';
  CarrierName?: string = '';
  CarrierContact?: string = '';
  /** Who it goes to. OutpassToCustomerId stays above; a typed new other party sends Id null and its name in OutpassToPartyName. */
  OutpassToType?: OutpassRecipientType | null = null;
  OutpassToSupplierId?: number | null = null;
  OutpassToPartyId?: number | null = null;
  OutpassToPartyName?: string = '';
  IsGateIn: boolean = false;
  ApprovedByName: string = '';
  OutpassItems: OutpassItemModel[] = [];
  CreateMode: string = 'Manual';
  BarcodeItems: OutpassBarcodeItemModel[] = [];
  BarcodeDetails: OutpassStockLabelModel[] = [];
  ReturnAcceptanceReason?: string; // Reason for accepting return for non-returnable outpass
  ReturnStatus?: string; // Calculated field: Not Returned, Fully Returned, Partially Returned
  IsVehicleGatedOut?: boolean; // Orphan flag: linked gate-in is GateOut=true but outpass not GateOutCompleted
  IsGatePassIssued?: boolean; // True when a linked, non-cancelled gate-in has the gate pass issued
  /** Release paper reels on this outpass (a sale); empty or missing when it carries none. */
  ReleasePaperReels?: OutpassReleasePaperReel[];
}

/** What to do with the part of a sold release paper reel's cost its jumbos did not carry. */
export type OutpassShortfallTreatment = 'WriteOff' | 'ReSpread';

/** One release paper reel on an outpass, as the approval dialog shows it. */
export interface OutpassReleasePaperReel {
  ReelNo: string;
  /** The rate per unit: the amount on the reel's line of the OutPass. */
  SaleRate?: number;
  /** The quantity on the reel's line. */
  Quantity?: number;
  /** `Kgs` / `Mtrs`, when the server sends it. */
  Unit?: string | null;
  /** Rate x quantity. */
  SaleValue: number;
  /** Cost already charged before this reel (merge / cut). */
  CostCarriedIn?: number | null;
  /** The opening part of the cost, always written off first. */
  OpeningWrittenOff?: number | null;
  /** False when no re-cost will run: re-spread shares wait for each order's next costing recalculation. */
  ReCostWillRun?: boolean | null;
  /** Purchase cost less the cost its jumbos carried less the sale value; negative is a gain. */
  Shortfall: number;
  /** The Release Paper Settings default, offered first. */
  DefaultTreatment: OutpassShortfallTreatment | string | null;
}




export class OutpassItemModel {
  OutpassItemId: number = 0;
  OutpassId?: number = 0;
  StockProductId?: number = 0;
  ProductId?: number = 0;
  ProductName: string = '';
  RackName: string = '';
  StoreName: string = '';
  BatchNo: string = '';
  Quantity: number = 0;
  Amount: number = 0;
  Total: number = 0;
  RackId?: number = 0;
  Unit: string = '';
  ReturnedQuantity: number = 0;
  ReturnedStoreId: number = 0;
  ReturnedRackId: number = 0;
  ReturnCompletedBy: UserModel = null;
  ReturnCompletedDate: string = '';
  OutpassType: string = '';
  ReturnedRackName: string = '';
  ReturnedStoreName: string = '';
  // New properties for enhanced return functionality
  EntryType?: string = '';
  SerialNo?: string = '';
  ShortCode?: string = '';
  IsManualItem?: boolean = true;
  // Consumption tracking properties
  ReasonForLessQuantity?: string = '';
  RequiresReason?: boolean = false;
  // ✅ NEW: StockLabelId for direct database relationship (used for barcode items stored in OutpassItemTable)
  StockLabelId?: number = 0;
}

export class OutpassStatusActionModel {
  OutpassId: number = 0;
  Status: string = '';
  Remark: string = '';
  /** Approve only, release paper reels only: the approver's choice for the shortfall. Null or missing uses the Settings default. */
  ReleasePaperShortfallTreatment?: OutpassShortfallTreatment | null;
}

// New models for barcode functionality
export class OutpassBarcodeItemModel {
  OutpassItemId: number = 0;
  StockLabelId: number = 0;
  SerialNo: string = '';
  ShortCode: string = '';
  ProductName: string = '';
  Quantity: number = 0;
  PackagingUnit: string = '';
  Amount: number = 0;
  PricePerUnit: number = 0;
  Unit: string = '';
  ProductId: number = 0;
  StockProductId: number = 0;
  BatchNo: string = '';
  CurrentStoreId: number = 0;
  CurrentRackId: number = 0;
  StoreName: string = '';
  RackName: string = '';

  // Return information properties (matching OutpassItemModel structure)
  ReturnedQuantity?: number = 0;
  ReturnedStoreId?: number = 0;
  ReturnedRackId?: number = 0;
  ReturnedStoreName?: string = '';
  ReturnedRackName?: string = '';
  ReturnCompletedBy?: string = '';
  ReturnCompletedDate?: string = '';
  ReasonForLessQuantity?: string = '';
  RequiresReason?: boolean = false;
}

export class BarcodeValidationResult {
  HasBarcodeLabels: boolean = false;
  BarcodeCount: number = 0;
  Message: string = '';
  BarcodeLabels: OutpassStockLabelModel[] = [];
}

/**
 * The label fields the outpass screens read back after a barcode scan — a
 * narrower shape than BarcodeLabelModel's StockLabelModel, which carries the
 * full label record. Named for its owner so both can live in one barrel.
 */
export class OutpassStockLabelModel {
  StockLabelId: number = 0;
  SerialNo: string = '';
  Quantity: number = 0;
  PackagingUnit: string = '';
  ProductName: string = '';
  BatchNo: string = '';
  ProductId: number = 0;
  StockProductId: number = 0;
  CurrentStoreId: number = 0;
  CurrentRackId: number = 0;
  StoreName: string = '';
  RackName: string = '';
  InspectionStatus: string = '';
  LabelStatus: string = '';
}

// New models for OutPass Print functionality with backend processing
export class OutpassPrintDataModel {
  OutpassDetails: OutPassModel = new OutPassModel();
  UnitGroups: PrintUnitGroupModel[] = [];
  Summary: PrintSummaryModel = new PrintSummaryModel();
}

export class PrintUnitGroupModel {
  UnitType: string = '';
  Products: PrintProductModel[] = [];
  TotalQuantity: number = 0;
  TotalAmount: number = 0;
  TotalLabels: number = 0;
  PackagingBreakdown: string = ''; // e.g., "3 Rolls, 2 Bags"
}

export class PrintProductModel {
  ProductId: number = 0;
  ProductName: string = '';
  Items: PrintItemModel[] = [];
  TotalQuantity: number = 0;
  TotalAmount: number = 0;
  LabelCount: number = 0;
  PackagingBreakdown: string = ''; // e.g., "2 Rolls, 1 Bag"
  HasBarcode: boolean = false;
  HasManual: boolean = false;
}

export class PrintItemModel {
  ProductId: number = 0;
  ProductName: string = '';
  BatchNo: string = '';
  Quantity: number = 0;
  Unit: string = '';
  Amount: number = 0;
  TotalAmount: number = 0;
  EntryType: string = ''; // "Manual" or "Barcode"
  StoreName: string = '';
  RackName: string = '';
  SerialNumbers: string[] = [];
  PackagingUnits: string[] = [];
  PackagingBreakdown: string = '';
  LabelCount: number = 0;
}

export class PrintSummaryModel {
  TotalProducts: number = 0;
  TotalManualItems: number = 0;
  TotalBarcodeItems: number = 0;
  TotalLabels: number = 0;
  GrandTotalQuantity: number = 0;
  GrandTotalAmount: number = 0;
  UnitsUsed: string[] = [];
  OverallPackagingBreakdown: string = '';
}